import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import { Square } from 'lucide-react';
import { CompassLogo } from './CompassLogo';

interface StreamingMessageProps {
  content: string;
  onCancel?: () => void;
}

/**
 * StreamingMessage component
 *
 * Displays a streaming response from the assistant with:
 * - Blinking cursor to indicate ongoing response
 * - Cancel button to stop streaming
 * - Real-time markdown rendering
 * - Accessibility attributes for screen readers
 *
 * Design: No layout animations during streaming for smooth, predictable UX.
 * Uses CSS transitions for subtle effects instead of spring physics.
 */
export const StreamingMessage = ({
  content,
  onCancel,
}: StreamingMessageProps) => {
  return (
    <div
      className="flex justify-start animate-in fade-in duration-150"
      role="log"
      aria-live="polite"
      aria-atomic="false"
      aria-busy="true"
      aria-label="Assistant is responding"
    >
      <div className="flex items-start gap-3">
        {/* Assistant logo - matches ChatMessage */}
        <div className="shrink-0 pt-1">
          <CompassLogo size={32} />
        </div>

        <div className="flex flex-col gap-1 flex-1 items-start">
          {/* Role label */}
          <p className="text-muted-foreground text-[13px] font-medium leading-normal px-1">
            Compass
          </p>

          {/* Message content */}
          <div className="w-full">
            <div className="prose prose-sm max-w-none dark:prose-invert prose-p:text-base prose-p:font-normal prose-p:leading-relaxed prose-p:my-2 first:prose-p:mt-0 last:prose-p:mb-0 prose-table:w-full prose-table:border-collapse prose-table:my-4 prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-2 prose-th:text-left prose-th:font-semibold prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2 px-1">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {content}
              </ReactMarkdown>
              {/* Streaming cursor - simple blink animation */}
              <span
                className="inline-block w-1.5 h-4 ml-0.5 bg-primary/70 animate-pulse rounded-sm"
                aria-hidden="true"
              />
            </div>
          </div>

          {/* Cancel button */}
          {onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="self-start text-muted-foreground hover:text-foreground gap-1.5 mt-1"
              aria-label="Stop generating response"
            >
              <Square className="h-3 w-3 fill-current" />
              <span>Stop generating</span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
